import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import CustomerDashboard from "@/pages/CustomerDashboard";
import CompanyDashboard from "@/pages/CompanyDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AboutPage from "@/pages/AboutPage";
import SupportPage from "@/pages/SupportPage";
import BlogPage from "@/pages/blog/BlogPage";
import BeylikduzuPage from "@/pages/blog/BeylikduzuPage";
import EsenyurtPage from "@/pages/blog/EsenyurtPage";
import AvcılarPage from "@/pages/blog/AvcılarPage";
import BuyukcekmecePage from "@/pages/blog/BuyukcekmecePage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// localStorage'dan token varsa her isteğe ekle
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("session_token");
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  return config;
});

// Auth Context
export const AuthContext = ({ children }) => {
  return children;
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`${API}/auth/session`, {
            session_id: sessionId
          });
          
          const { user } = response.data;
          
          if (user.role === "company") {
            navigate("/company", { state: { user }, replace: true });
          } else if (user.role === "admin") {
            navigate("/admin", { state: { user }, replace: true });
          } else {
            navigate("/dashboard", { state: { user }, replace: true });
          }
        } catch (error) {
          console.error("Auth error:", error);
          navigate("/auth", { replace: true });
        }
      } else {
        // session_id yok — mevcut cookie oturumunu kontrol et (PWA ana ekran açılışı)
        try {
          const response = await axios.get(`${API}/auth/me`);
          const user = response.data;
          if (user.role === "company") {
            navigate("/company", { state: { user }, replace: true });
          } else if (user.role === "admin") {
            navigate("/admin", { state: { user }, replace: true });
          } else {
            navigate("/dashboard", { state: { user }, replace: true });
          }
        } catch (error) {
          navigate("/auth", { replace: true });
        }
      }
    };

    processAuth();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Giriş yapılıyor...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/auth", { replace: true });
      }
    };

    checkAuth();
  }, [location, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    if (user?.role === "company") {
      return <Navigate to="/company" replace />;
    } else if (user?.role === "admin") {
      return <Navigate to="/admin" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children({ user, setUser });
};

// Ana sayfaya gelince token varsa panele yönlendir, yoksa LandingPage göster
const HomeRedirect = () => {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("session_token");
    if (!token) {
      setReady(true);
      return;
    }
    axios.get(`${API}/auth/me`)
      .then(res => {
        const user = res.data;
        if (user.role === "company") navigate("/company", { state: { user }, replace: true });
        else if (user.role === "admin") navigate("/admin", { state: { user }, replace: true });
        else navigate("/dashboard", { state: { user }, replace: true });
      })
      .catch(() => {
        localStorage.removeItem("session_token");
        setReady(true);
      });
  }, [navigate]);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return <LandingPage />;
};

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/hakkimizda" element={<AboutPage />} />
      <Route path="/about" element={<Navigate to="/hakkimizda" replace />} />
      <Route path="/destek" element={<SupportPage />} />
      <Route path="/support" element={<Navigate to="/destek" replace />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/beylikduzu-hali-yikama" element={<BeylikduzuPage />} />
      <Route path="/blog/esenyurt-hali-yikama" element={<EsenyurtPage />} />
      <Route path="/blog/avcilar-hali-yikama" element={<AvcılarPage />} />
      <Route path="/blog/buyukcekmeceye-hali-yikama" element={<BuyukcekmecePage />} />
      <Route
        path="/dashboard/*"
        element={
          <ProtectedRoute allowedRoles={["customer"]}>
            {({ user, setUser }) => <CustomerDashboard user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/company/*"
        element={
          <ProtectedRoute allowedRoles={["company"]}>
            {({ user, setUser }) => <CompanyDashboard user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            {({ user, setUser }) => <AdminDashboard user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;

export { API, BACKEND_URL };
